/**
 * Opportunity Solution Tree Store
 * 
 * Manages OST trees and nodes with database persistence.
 * Integrates with DEGAP cycles by allowing solutions to become candidates.
 * 
 * Based on Teresa Torres' Continuous Discovery Habits framework:
 * - Outcome: Business goal/metric to achieve
 * - Opportunity: Customer needs, pain points, desires
 * - Solution: Product features, improvements
 * - Experiment: Tests to validate solutions
 */

import { create } from 'zustand';
import { getSafeSupabaseClient, generateUUID, getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { dispatchStoreWarning } from '../../hooks/prioritization/useStoreErrorHandler';

// ============================================================================
// Types
// ============================================================================

export type OSTNodeType = 'outcome' | 'opportunity' | 'solution' | 'experiment';

export type OpportunitySource = 
  | 'customer_interview'
  | 'support_ticket'
  | 'sales_conversation'
  | 'analytics'
  | 'survey'
  | 'user_research'
  | 'assumption';

export type ExperimentType = 
  | 'ab_test'
  | 'prototype_test'
  | 'fake_door'
  | 'concierge'
  | 'wizard_of_oz'
  | 'landing_page'
  | 'user_interview'
  | 'survey'
  | 'mvp';

export type ExperimentStatus = 'planned' | 'in_progress' | 'completed' | 'inconclusive';
export type ValidationStatus = 'not_started' | 'in_progress' | 'validated' | 'invalidated' | 'needs_more_data';

export interface OSTTree {
  id: string;
  workspaceId: string | null;
  name: string;
  description?: string;
  teamName?: string;
  productArea?: string;
  quarter?: string;
  lastInterviewDate?: Date;
  interviewCount: number;
  ownerId?: string;
  createdBy?: string;
  /** Optional: the canonical Strategy this OST tree sits under ("opportunity sits under Strategy"). */
  strategyId?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface OSTNode {
  id: string;
  treeId: string;
  nodeType: OSTNodeType;
  title: string;
  description?: string;
  parentId: string | null;
  position: { x: number; y: number };
  collapsed: boolean;
  sortOrder: number;
  
  // Outcome fields
  metric?: string;
  target?: string;
  currentValue?: string;
  timeframe?: string;
  
  // Opportunity fields
  source?: OpportunitySource;
  customerQuote?: string;
  frequency?: string;
  severity?: string;
  customerSegments?: string[];
  isTargeted?: boolean;
  
  // Solution fields
  validationStatus?: ValidationStatus;
  effort?: string;
  confidence?: number;
  assumptions?: string[];
  
  // Experiment fields
  experimentType?: ExperimentType;
  experimentStatus?: ExperimentStatus;
  hypothesis?: string;
  successCriteria?: string;
  results?: string;
  learnings?: string;
  startDate?: Date;
  endDate?: Date;
  
  // Context (from view)
  treeName?: string;
  parentOpportunityTitle?: string;
  outcomeTitle?: string;

  /**
   * FK to items.id — populated for opportunity and solution nodes.
   * The linked item carries item_category matching node_type and source='ost'.
   */
  itemId?: string | null;
  
  createdBy?: string;
  createdAt: Date;
  updatedAt: Date;
}

interface OSTState {
  trees: OSTTree[];
  nodes: OSTNode[];
  activeTreeId: string | null;
  isLoading: boolean;
  error: string | null;
  
  // Tree CRUD
  fetchTrees: (workspaceId?: string) => Promise<void>;
  fetchTree: (treeId: string) => Promise<OSTTree | null>;
  createTree: (params: {
    name: string;
    description?: string;
    workspaceId?: string;
    ownerId?: string;
    /** Canonical Strategy this tree sits under (see ost_trees.strategy_id). */
    strategyId?: string | null;
    metadata?: {
      teamName?: string;
      productArea?: string;
      quarter?: string;
    };
  }) => Promise<OSTTree | null>;
  updateTree: (treeId: string, updates: Partial<OSTTree>) => Promise<boolean>;
  deleteTree: (treeId: string) => Promise<boolean>;
  setActiveTree: (treeId: string | null) => void;
  
  // Node CRUD
  fetchNodes: (treeId: string) => Promise<void>;
  fetchNodesWithContext: (treeId: string) => Promise<void>;
  createNode: (params: {
    treeId: string;
    nodeType: OSTNodeType;
    title: string;
    description?: string;
    parentId?: string | null;
    createdBy?: string;
    // Type-specific fields
    [key: string]: unknown;
  }) => Promise<OSTNode | null>;
  updateNode: (nodeId: string, updates: Partial<OSTNode>) => Promise<boolean>;
  deleteNode: (nodeId: string) => Promise<boolean>;
  
  // Targeting
  setTargetedOpportunity: (nodeId: string) => Promise<boolean>;
  clearTargetedOpportunity: (treeId: string) => Promise<boolean>;
  
  // Item attachment
  /**
   * Attach an existing Item record to an opportunity or solution node.
   * Sets ost_nodes.item_id and items.ost_node_id; syncs node title from item.
   * Only items whose item_category matches the node type are accepted.
   */
  attachItemToNode: (nodeId: string, itemId: string) => Promise<boolean>;
  /**
   * Remove the item link from a node (ost_nodes.item_id → null,
   * items.ost_node_id → null). The item itself is not deleted.
   */
  detachItemFromNode: (nodeId: string) => Promise<boolean>;
  
  // Selectors
  getTree: (treeId: string) => OSTTree | undefined;
  getNodesForTree: (treeId: string) => OSTNode[];
  getNode: (nodeId: string) => OSTNode | undefined;
  getChildren: (nodeId: string) => OSTNode[];
  getTargetedOpportunity: (treeId: string) => OSTNode | undefined;
  getSolutionsForOpportunity: (opportunityId: string) => OSTNode[];
  getValidatedSolutions: (treeId: string) => OSTNode[];
}

// ============================================================================
// Database Helpers
// ============================================================================

function dbToTree(row: Record<string, unknown>): OSTTree {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string | null,
    name: row.name as string,
    description: row.description as string | undefined,
    teamName: row.team_name as string | undefined,
    productArea: row.product_area as string | undefined,
    quarter: row.quarter as string | undefined,
    lastInterviewDate: row.last_interview_date ? new Date(row.last_interview_date as string) : undefined,
    interviewCount: (row.interview_count as number) || 0,
    ownerId: row.owner_id as string | undefined,
    createdBy: row.created_by as string | undefined,
    strategyId: (row.strategy_id as string) ?? null,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
  };
}

function dbToNode(row: Record<string, unknown>): OSTNode {
  const position = (row.position as { x: number; y: number }) || { x: 0, y: 0 };
  
  return {
    id: row.id as string,
    treeId: row.tree_id as string,
    nodeType: row.node_type as OSTNodeType,
    title: row.title as string,
    description: row.description as string | undefined,
    parentId: row.parent_id as string | null,
    position,
    collapsed: row.collapsed as boolean || false,
    sortOrder: (row.sort_order as number) || 0,
    
    // Outcome fields
    metric: row.metric as string | undefined,
    target: row.target as string | undefined,
    currentValue: row.current_value as string | undefined,
    timeframe: row.timeframe as string | undefined,
    
    // Opportunity fields
    source: row.source as OpportunitySource | undefined,
    customerQuote: row.customer_quote as string | undefined,
    frequency: row.frequency as string | undefined,
    severity: row.severity as string | undefined,
    customerSegments: row.customer_segments as string[] | undefined,
    isTargeted: row.is_targeted as boolean | undefined,
    
    // Solution fields
    validationStatus: row.validation_status as ValidationStatus | undefined,
    effort: row.effort as string | undefined,
    confidence: row.confidence as number | undefined,
    assumptions: row.assumptions as string[] | undefined,
    
    // Experiment fields
    experimentType: row.experiment_type as ExperimentType | undefined,
    experimentStatus: row.experiment_status as ExperimentStatus | undefined,
    hypothesis: row.hypothesis as string | undefined,
    successCriteria: row.success_criteria as string | undefined,
    results: row.results as string | undefined,
    learnings: row.learnings as string | undefined,
    startDate: row.start_date ? new Date(row.start_date as string) : undefined,
    endDate: row.end_date ? new Date(row.end_date as string) : undefined,
    
    // Context from view
    treeName: row.tree_name as string | undefined,
    parentOpportunityTitle: row.parent_opportunity_title as string | undefined,
    outcomeTitle: row.outcome_title as string | undefined,

    itemId: (row.item_id as string) ?? null,

    createdBy: row.created_by as string | undefined,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
  };
}

// ============================================================================
// Store
// ============================================================================

export const useOSTStore = create<OSTState>()((set, get) => ({
  trees: [],
  nodes: [],
  activeTreeId: null,
  isLoading: false,
  error: null,

  // ========================================================================
  // Tree CRUD
  // ========================================================================

  fetchTrees: async (workspaceId?: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    set({ isLoading: true, error: null });

    try {
      const effectiveWorkspaceId = workspaceId ?? getCurrentWorkspaceId() ?? undefined;
      let query = supabase.from('ost_trees').select('*');
      if (effectiveWorkspaceId) {
        query = query.or(`workspace_id.eq.${effectiveWorkspaceId},workspace_id.is.null`);
      }

      const { data, error } = await query.order('updated_at', { ascending: false });

      if (error) {
        console.error('[OSTStore] Fetch trees error:', error);
        set({ isLoading: false, error: error.message });
        return;
      }

      set({
        trees: (data || []).map(dbToTree),
        isLoading: false,
        error: null,
      });

      console.log(`[OSTStore] Fetched ${data?.length || 0} trees`);
    } catch (err) {
      console.error('[OSTStore] Fetch trees exception:', err);
      set({ isLoading: false, error: String(err) });
    }
  },

  fetchTree: async (treeId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return null;

    try {
      const { data, error } = await supabase
        .from('ost_trees')
        .select('*')
        .eq('id', treeId)
        .single();

      if (error) {
        console.error('[OSTStore] Fetch tree error:', error);
        return null;
      }

      const tree = dbToTree(data);
      
      set(state => ({
        trees: state.trees.some(t => t.id === treeId)
          ? state.trees.map(t => t.id === treeId ? tree : t)
          : [...state.trees, tree],
      }));

      return tree;
    } catch (err) {
      console.error('[OSTStore] Fetch tree exception:', err);
      return null;
    }
  },

  createTree: async (params) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return null;

    const now = new Date();
    const workspaceId = params.workspaceId ?? getCurrentWorkspaceId() ?? null;
    const newTree: Record<string, unknown> = {
      id: generateUUID(),
      workspace_id: workspaceId,
      name: params.name,
      description: params.description || null,
      team_name: params.metadata?.teamName || null,
      product_area: params.metadata?.productArea || null,
      quarter: params.metadata?.quarter || null,
      owner_id: params.ownerId || null,
      created_by: params.ownerId || null,
      created_at: now.toISOString(),
      updated_at: now.toISOString(),
    };
    if (params.strategyId !== undefined && params.strategyId !== null) {
      newTree.strategy_id = params.strategyId;
    }

    try {
      const { data, error } = await supabase
        .from('ost_trees')
        .insert(newTree)
        .select()
        .single();

      if (error) {
        console.error('[OSTStore] Create tree error:', error);
        console.error('[OSTStore] Error details - code:', error.code, 'message:', error.message, 'details:', error.details, 'hint:', error.hint);
        return null;
      }

      const tree = dbToTree(data);
      set(state => ({ trees: [tree, ...state.trees] }));
      
      console.log(`[OSTStore] Created tree: ${tree.name}`);
      return tree;
    } catch (err) {
      console.error('[OSTStore] Create tree exception:', err);
      return null;
    }
  },

  updateTree: async (treeId, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    const dbUpdates: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    };

    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    if (updates.teamName !== undefined) dbUpdates.team_name = updates.teamName;
    if (updates.productArea !== undefined) dbUpdates.product_area = updates.productArea;
    if (updates.quarter !== undefined) dbUpdates.quarter = updates.quarter;
    if (updates.interviewCount !== undefined) dbUpdates.interview_count = updates.interviewCount;
    if (updates.strategyId !== undefined) dbUpdates.strategy_id = updates.strategyId;

    try {
      const { error } = await supabase
        .from('ost_trees')
        .update(dbUpdates)
        .eq('id', treeId);

      if (error) {
        console.error('[OSTStore] Update tree error:', error);
        return false;
      }

      set(state => ({
        trees: state.trees.map(t =>
          t.id === treeId ? { ...t, ...updates, updatedAt: new Date() } : t
        ),
      }));

      return true;
    } catch (err) {
      console.error('[OSTStore] Update tree exception:', err);
      return false;
    }
  },

  deleteTree: async (treeId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    try {
      const { error } = await supabase
        .from('ost_trees')
        .delete()
        .eq('id', treeId);

      if (error) {
        console.error('[OSTStore] Delete tree error:', error);
        return false;
      }

      set(state => ({
        trees: state.trees.filter(t => t.id !== treeId),
        nodes: state.nodes.filter(n => n.treeId !== treeId),
        activeTreeId: state.activeTreeId === treeId ? null : state.activeTreeId,
      }));

      console.log(`[OSTStore] Deleted tree: ${treeId}`);
      return true;
    } catch (err) {
      console.error('[OSTStore] Delete tree exception:', err);
      return false;
    }
  },

  setActiveTree: (treeId) => {
    set({ activeTreeId: treeId });
  },

  // ========================================================================
  // Node CRUD
  // ========================================================================

  fetchNodes: async (treeId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    set({ isLoading: true, error: null });

    try {
      const { data, error } = await supabase
        .from('ost_nodes')
        .select('*')
        .eq('tree_id', treeId)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('[OSTStore] Fetch nodes error:', error);
        set({ isLoading: false, error: error.message });
        return;
      }

      const fetched = (data || []).map(dbToNode);
      
      set(state => {
        const otherNodes = state.nodes.filter(n => n.treeId !== treeId);
        return {
          nodes: [...otherNodes, ...fetched],
          isLoading: false,
          error: null,
        };
      });

      console.log(`[OSTStore] Fetched ${fetched.length} nodes for tree ${treeId}`);
    } catch (err) {
      console.error('[OSTStore] Fetch nodes exception:', err);
      set({ isLoading: false, error: String(err) });
    }
  },

  fetchNodesWithContext: async (treeId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    set({ isLoading: true, error: null });

    try {
      const { data, error } = await supabase
        .from('ost_nodes_with_context')
        .select('*')
        .eq('tree_id', treeId)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('[OSTStore] Fetch nodes with context error:', error);
        set({ isLoading: false, error: error.message });
        return;
      }

      const fetched = (data || []).map(dbToNode);
      
      set(state => {
        const otherNodes = state.nodes.filter(n => n.treeId !== treeId);
        return {
          nodes: [...otherNodes, ...fetched],
          isLoading: false,
          error: null,
        };
      });

      console.log(`[OSTStore] Fetched ${fetched.length} nodes with context for tree ${treeId}`);
    } catch (err) {
      console.error('[OSTStore] Fetch nodes with context exception:', err);
      set({ isLoading: false, error: String(err) });
    }
  },

  createNode: async (params) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return null;

    const now = new Date();
    
    // Build node object based on type
    const newNode: Record<string, unknown> = {
      id: generateUUID(),
      tree_id: params.treeId,
      node_type: params.nodeType,
      title: params.title,
      description: params.description || null,
      parent_id: params.parentId || null,
      created_by: params.createdBy || null,
      created_at: now.toISOString(),
      updated_at: now.toISOString(),
    };

    // Add type-specific fields
    if (params.nodeType === 'outcome') {
      if (params.metric) newNode.metric = params.metric;
      if (params.target) newNode.target = params.target;
      if (params.currentValue) newNode.current_value = params.currentValue;
      if (params.timeframe) newNode.timeframe = params.timeframe;
    } else if (params.nodeType === 'opportunity') {
      if (params.source) newNode.source = params.source;
      if (params.customerQuote) newNode.customer_quote = params.customerQuote;
      if (params.frequency) newNode.frequency = params.frequency;
      if (params.severity) newNode.severity = params.severity;
      if (params.customerSegments) newNode.customer_segments = params.customerSegments;
      if (params.isTargeted) newNode.is_targeted = params.isTargeted;
    } else if (params.nodeType === 'solution') {
      newNode.validation_status = params.validationStatus || 'not_started';
      if (params.effort) newNode.effort = params.effort;
      if (params.confidence) newNode.confidence = params.confidence;
      if (params.assumptions) newNode.assumptions = params.assumptions;
    } else if (params.nodeType === 'experiment') {
      if (params.experimentType) newNode.experiment_type = params.experimentType;
      newNode.experiment_status = params.experimentStatus || 'planned';
      if (params.hypothesis) newNode.hypothesis = params.hypothesis;
      if (params.successCriteria) newNode.success_criteria = params.successCriteria;
    }

    // ── Step 1: Insert the ost_node first (no item_id yet) ────────────────────
    // The item FK (candidates_ost_node_id_fkey) requires ost_nodes.id to exist
    // before items.ost_node_id can reference it. Insert node first, then item.
    let insertedNode: OSTNode | null = null;
    try {
      const { data, error } = await supabase
        .from('ost_nodes')
        .insert(newNode)
        .select()
        .single();

      if (error) {
        console.error('[OSTStore] Create node error:', error);
        return null;
      }

      insertedNode = dbToNode(data);
      set(state => ({ nodes: [...state.nodes, insertedNode!] }));
      console.log(`[OSTStore] Created ${params.nodeType} node: ${params.title}`);
    } catch (err) {
      console.error('[OSTStore] Create node exception:', err);
      return null;
    }

    // ── Steps 2 & 3 (auto-create linked item) are intentionally disabled ─────
    // Items should be linked manually by the user from the node editor, not
    // auto-created on node creation. An auto-created item has an empty title
    // which produces a confusing "(untitled)" entry. The user attaches an
    // existing Item (or creates one first) via the "Linked Item" picker.

    return insertedNode;
  },

  updateNode: async (nodeId, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    const dbUpdates: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    };

    // Map updates to DB columns
    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    if (updates.parentId !== undefined) dbUpdates.parent_id = updates.parentId;
    if (updates.position !== undefined) dbUpdates.position = updates.position;
    if (updates.collapsed !== undefined) dbUpdates.collapsed = updates.collapsed;
    if (updates.sortOrder !== undefined) dbUpdates.sort_order = updates.sortOrder;
    
    // Outcome fields
    if (updates.metric !== undefined) dbUpdates.metric = updates.metric;
    if (updates.target !== undefined) dbUpdates.target = updates.target;
    if (updates.currentValue !== undefined) dbUpdates.current_value = updates.currentValue;
    if (updates.timeframe !== undefined) dbUpdates.timeframe = updates.timeframe;
    
    // Opportunity fields
    if (updates.source !== undefined) dbUpdates.source = updates.source;
    if (updates.customerQuote !== undefined) dbUpdates.customer_quote = updates.customerQuote;
    if (updates.frequency !== undefined) dbUpdates.frequency = updates.frequency;
    if (updates.severity !== undefined) dbUpdates.severity = updates.severity;
    if (updates.customerSegments !== undefined) dbUpdates.customer_segments = updates.customerSegments;
    if (updates.isTargeted !== undefined) dbUpdates.is_targeted = updates.isTargeted;
    
    // Solution fields
    if (updates.validationStatus !== undefined) dbUpdates.validation_status = updates.validationStatus;
    if (updates.effort !== undefined) dbUpdates.effort = updates.effort;
    if (updates.confidence !== undefined) dbUpdates.confidence = updates.confidence;
    if (updates.assumptions !== undefined) dbUpdates.assumptions = updates.assumptions;
    
    // Experiment fields
    if (updates.experimentType !== undefined) dbUpdates.experiment_type = updates.experimentType;
    if (updates.experimentStatus !== undefined) dbUpdates.experiment_status = updates.experimentStatus;
    if (updates.hypothesis !== undefined) dbUpdates.hypothesis = updates.hypothesis;
    if (updates.successCriteria !== undefined) dbUpdates.success_criteria = updates.successCriteria;
    if (updates.results !== undefined) dbUpdates.results = updates.results;
    if (updates.learnings !== undefined) dbUpdates.learnings = updates.learnings;

    // Item link
    if (updates.itemId !== undefined) dbUpdates.item_id = updates.itemId;

    try {
      const { error } = await supabase
        .from('ost_nodes')
        .update(dbUpdates)
        .eq('id', nodeId);

      if (error) {
        console.error('[OSTStore] Update node error:', error);
        return false;
      }

      // Sync title and/or description to the linked canonical Item (if any)
      const existingNode = get().nodes.find(n => n.id === nodeId);
      if (existingNode?.itemId && (updates.title !== undefined || updates.description !== undefined)) {
        const itemUpdates: Record<string, unknown> = {
          updated_at: new Date().toISOString(),
        };
        if (updates.title !== undefined) itemUpdates.title = updates.title || '(untitled)';
        if (updates.description !== undefined) itemUpdates.description = updates.description ?? null;

        supabase
          .from('items')
          .update(itemUpdates)
          .eq('id', existingNode.itemId)
          .then(({ error: itemErr }) => {
            if (itemErr) console.warn('[OSTStore] Item sync on update failed:', itemErr);
          });
      }

      set(state => ({
        nodes: state.nodes.map(n =>
          n.id === nodeId ? { ...n, ...updates, updatedAt: new Date() } : n
        ),
      }));

      return true;
    } catch (err) {
      console.error('[OSTStore] Update node exception:', err);
      return false;
    }
  },

  deleteNode: async (nodeId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    try {
      // Capture node data before deletion for item cleanup
      const nodeToDelete = get().nodes.find(n => n.id === nodeId);

      const { error } = await supabase
        .from('ost_nodes')
        .delete()
        .eq('id', nodeId);

      if (error) {
        console.error('[OSTStore] Delete node error:', error);
        return false;
      }

      // If the node had a linked item, clear its ost_node_id back-reference so
      // the item remains in the items table (it may have been shaped/ranked) but
      // is no longer tied to a deleted node.
      if (nodeToDelete?.itemId) {
        supabase
          .from('items')
          .update({ ost_node_id: null, updated_at: new Date().toISOString() })
          .eq('id', nodeToDelete.itemId)
          .then(({ error: itemErr }) => {
            if (itemErr) console.warn('[OSTStore] Item unlink on node delete failed:', itemErr);
          });
      }

      // Remove node and all descendants (cascade handled by DB, but clean local state)
      if (nodeToDelete) {
        const descendantIds = new Set<string>();
        const findDescendants = (parentId: string) => {
          get().nodes.forEach(n => {
            if (n.parentId === parentId) {
              descendantIds.add(n.id);
              findDescendants(n.id);
            }
          });
        };
        findDescendants(nodeId);
        
        set(state => ({
          nodes: state.nodes.filter(n => n.id !== nodeId && !descendantIds.has(n.id)),
        }));
      }

      console.log(`[OSTStore] Deleted node: ${nodeId}`);
      return true;
    } catch (err) {
      console.error('[OSTStore] Delete node exception:', err);
      return false;
    }
  },

  // ========================================================================
  // Item Attachment
  // ========================================================================

  attachItemToNode: async (nodeId, itemId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    const node = get().nodes.find(n => n.id === nodeId);
    if (!node) return false;
    if (node.nodeType !== 'opportunity' && node.nodeType !== 'solution') return false;

    try {
      // Fetch the item to validate category and get its title
      const { data: itemRow, error: fetchErr } = await supabase
        .from('items')
        .select('id, title, description, item_category')
        .eq('id', itemId)
        .single();

      if (fetchErr || !itemRow) {
        console.error('[OSTStore] attachItemToNode: item not found', fetchErr);
        return false;
      }

      // Enforce category match: item_category must match node type
      if (itemRow.item_category && itemRow.item_category !== node.nodeType) {
        console.warn(
          `[OSTStore] attachItemToNode: category mismatch — node is '${node.nodeType}' but item category is '${itemRow.item_category}'`
        );
        dispatchStoreWarning(
          'OST',
          'attachItemToNode',
          `Cannot attach: item category '${itemRow.item_category}' does not match node type '${node.nodeType}'.`
        );
        return false;
      }

      // If item has no category yet, stamp it with the node type
      if (!itemRow.item_category) {
        await supabase
          .from('items')
          .update({ item_category: node.nodeType, updated_at: new Date().toISOString() })
          .eq('id', itemId);
      }

      // If the item was already linked to a different node, clear that node's item_id first
      const { data: oldNode } = await supabase
        .from('ost_nodes')
        .select('id')
        .eq('item_id', itemId)
        .neq('id', nodeId)
        .maybeSingle();
      if (oldNode) {
        await supabase
          .from('ost_nodes')
          .update({ item_id: null, updated_at: new Date().toISOString() })
          .eq('id', oldNode.id);
      }

      const now = new Date().toISOString();

      // Link: ost_node.item_id and items.ost_node_id
      const [nodeUpdate, itemUpdate] = await Promise.all([
        supabase
          .from('ost_nodes')
          .update({ item_id: itemId, title: itemRow.title, updated_at: now })
          .eq('id', nodeId),
        supabase
          .from('items')
          .update({ ost_node_id: nodeId, updated_at: now })
          .eq('id', itemId),
      ]);

      if (nodeUpdate.error || itemUpdate.error) {
        console.error('[OSTStore] attachItemToNode update failed', nodeUpdate.error, itemUpdate.error);
        return false;
      }

      // Update local OST state
      set(state => ({
        nodes: state.nodes.map(n =>
          n.id === nodeId
            ? { ...n, itemId, title: itemRow.title as string, description: (itemRow.description as string) ?? n.description, updatedAt: new Date() }
            : n
        ),
      }));

      console.log(`[OSTStore] Attached item ${itemId} to node ${nodeId}`);
      return true;
    } catch (err) {
      console.error('[OSTStore] attachItemToNode exception:', err);
      return false;
    }
  },

  detachItemFromNode: async (nodeId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    const node = get().nodes.find(n => n.id === nodeId);
    if (!node || !node.itemId) return false;

    const itemId = node.itemId;
    const now = new Date().toISOString();

    try {
      const [nodeUpdate, itemUpdate] = await Promise.all([
        supabase
          .from('ost_nodes')
          .update({ item_id: null, updated_at: now })
          .eq('id', nodeId),
        supabase
          .from('items')
          .update({ ost_node_id: null, updated_at: now })
          .eq('id', itemId),
      ]);

      if (nodeUpdate.error || itemUpdate.error) {
        console.error('[OSTStore] detachItemFromNode failed', nodeUpdate.error, itemUpdate.error);
        return false;
      }

      set(state => ({
        nodes: state.nodes.map(n =>
          n.id === nodeId ? { ...n, itemId: null, updatedAt: new Date() } : n
        ),
      }));

      console.log(`[OSTStore] Detached item ${itemId} from node ${nodeId}`);
      return true;
    } catch (err) {
      console.error('[OSTStore] detachItemFromNode exception:', err);
      return false;
    }
  },

  // ========================================================================
  // Targeting
  // ========================================================================

  setTargetedOpportunity: async (nodeId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    const node = get().nodes.find(n => n.id === nodeId);
    if (!node || node.nodeType !== 'opportunity') return false;

    try {
      // First, clear any existing targeted opportunity in this tree
      await supabase
        .from('ost_nodes')
        .update({ is_targeted: false })
        .eq('tree_id', node.treeId)
        .eq('node_type', 'opportunity');

      // Then set this one as targeted
      const { error } = await supabase
        .from('ost_nodes')
        .update({ is_targeted: true, updated_at: new Date().toISOString() })
        .eq('id', nodeId);

      if (error) {
        console.error('[OSTStore] Set targeted error:', error);
        return false;
      }

      set(state => ({
        nodes: state.nodes.map(n => ({
          ...n,
          isTargeted: n.id === nodeId ? true : (n.treeId === node.treeId && n.nodeType === 'opportunity' ? false : n.isTargeted),
        })),
      }));

      console.log(`[OSTStore] Set targeted opportunity: ${node.title}`);
      return true;
    } catch (err) {
      console.error('[OSTStore] Set targeted exception:', err);
      return false;
    }
  },

  clearTargetedOpportunity: async (treeId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    try {
      const { error } = await supabase
        .from('ost_nodes')
        .update({ is_targeted: false, updated_at: new Date().toISOString() })
        .eq('tree_id', treeId)
        .eq('node_type', 'opportunity');

      if (error) {
        console.error('[OSTStore] Clear targeted error:', error);
        return false;
      }

      set(state => ({
        nodes: state.nodes.map(n =>
          n.treeId === treeId && n.nodeType === 'opportunity'
            ? { ...n, isTargeted: false }
            : n
        ),
      }));

      return true;
    } catch (err) {
      console.error('[OSTStore] Clear targeted exception:', err);
      return false;
    }
  },

  // ========================================================================
  // Selectors
  // ========================================================================

  getTree: (treeId) => get().trees.find(t => t.id === treeId),
  
  getNodesForTree: (treeId) => get().nodes.filter(n => n.treeId === treeId),
  
  getNode: (nodeId) => get().nodes.find(n => n.id === nodeId),
  
  getChildren: (nodeId) => get().nodes.filter(n => n.parentId === nodeId),
  
  getTargetedOpportunity: (treeId) => 
    get().nodes.find(n => n.treeId === treeId && n.nodeType === 'opportunity' && n.isTargeted),
  
  getSolutionsForOpportunity: (opportunityId) => 
    get().nodes.filter(n => n.parentId === opportunityId && n.nodeType === 'solution'),
  
  getValidatedSolutions: (treeId) => 
    get().nodes.filter(n => 
      n.treeId === treeId && 
      n.nodeType === 'solution' && 
      n.validationStatus === 'validated'
    ),
}));

// ============================================================================
// Integration Helper: Create candidate from OST solution
// ============================================================================

export interface CreateCandidateFromSolutionParams {
  solution: OSTNode;
  opportunity: OSTNode;
  outcome: OSTNode;
  cycleId?: string;
  createdBy: string;
}

/**
 * Creates a candidate from an OST solution, linking back to the source nodes.
 * Returns the candidate data to be passed to itemsStore.addCandidate().
 */
export function buildCandidateFromSolution(params: CreateCandidateFromSolutionParams) {
  const { solution, opportunity, outcome } = params;
  
  // Build description with strategic context
  const descriptionParts = [
    solution.description || '',
    '',
    '---',
    `**Opportunity:** ${opportunity.title}`,
    opportunity.description ? `> ${opportunity.description}` : '',
    opportunity.customerQuote ? `> Customer: "${opportunity.customerQuote}"` : '',
    '',
    `**Outcome:** ${outcome.title}`,
    outcome.metric ? `Target: ${outcome.target} (Current: ${outcome.currentValue})` : '',
  ].filter(Boolean);

  if (solution.assumptions?.length) {
    descriptionParts.push('', '**Assumptions to validate:**');
    solution.assumptions.forEach(a => descriptionParts.push(`- ${a}`));
  }

  return {
    title: solution.title,
    description: descriptionParts.join('\n'),
    candidateType: 'idea' as const,
    source: 'ost' as const,
    ostNodeId: solution.id,
    ostOpportunityId: opportunity.id,
    createdBy: params.createdBy,
    metadata: {
      ostTreeId: solution.treeId,
      ostOutcomeId: outcome.id,
      ostSolutionTitle: solution.title,
      ostOpportunityTitle: opportunity.title,
      ostOutcomeTitle: outcome.title,
      validationStatus: solution.validationStatus,
      effort: solution.effort,
      confidence: solution.confidence,
    },
  };
}
